(() => {
  class BootyliftVideoStories extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bl-video-stories__track');
      if (!this.track) return;
      this.events = new AbortController();
      this.options = {signal: this.events.signal};
      this.rail = this.querySelector('.bl-video-stories__rail');
      this.originals = [...this.rail.children];
      this.index = 0;
      this.copies = -1;
      this.lastWidth = null;
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motionPreference.matches;
      this.pauseUntil = 0;
      this.speed = 0.026; // 26px/second, independent of screen refresh rate.
      this.nearViewport = false;
      this.previous = this.querySelector('.bl-video-stories__previous');
      this.next = this.querySelector('.bl-video-stories__next');
      this.motionButton = this.querySelector('.bl-video-stories__motion');
      this.posterObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const poster = entry.target.querySelector('.bl-video-stories__poster');
          if (poster?.dataset.src) {
            poster.decoding = 'async';
            if (poster.dataset.srcset) poster.srcset = poster.dataset.srcset;
            poster.src = poster.dataset.src;
          }
          this.posterObserver.unobserve(entry.target);
        });
      }, {rootMargin: '250px 0px', threshold: 0});
      this.visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.target.inert = entry.intersectionRatio < 0.15;
          if (entry.target.inert) entry.target.querySelector('video')?.pause();
        });
      }, {threshold: [0, 0.15]});
      this.originals.forEach(card => this.bindMedia(card));
      this.previous.addEventListener('click', () => { this.updateControls(); this.go(this.index - 1); }, this.options);
      this.next.addEventListener('click', () => { this.updateControls(); this.go(this.index + 1); }, this.options);
      this.track.addEventListener('scroll', () => {
        this.updateControls();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.recenter(), 150);
      }, {...this.options, passive: true});
      this.track.addEventListener('scrollend', () => this.recenter(), this.options);
      this.track.addEventListener('pointerdown', () => {
        this.dragging = true;
        this.stopMotion();
        this.pauseUntil = performance.now() + 8000;
        this.track.classList.remove('is-auto-scrolling');
        this.startMotion();
      }, this.options);
      this.track.addEventListener('wheel', () => { this.stopMotion(); this.pauseUntil = performance.now() + 8000; this.startMotion(); }, {...this.options, passive: true});
      const release = () => { if (this.dragging) this.pauseUntil = performance.now() + 8000; this.dragging = false; this.startMotion(); };
      window.addEventListener('pointerup', release, this.options);
      window.addEventListener('pointercancel', release, this.options);
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovering = true; this.startMotion(); } }, this.options);
      this.addEventListener('pointerleave', () => { this.hovering = false; this.pauseUntil = performance.now() + 2000; this.startMotion(); }, this.options);
      this.addEventListener('focusin', () => { this.keyboardFocus = this.matches(':focus-visible') || !!this.querySelector(':focus-visible'); this.startMotion(); }, this.options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.keyboardFocus = false; this.startMotion(); }, this.options);
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        this.updateControls();
        const destinations = {ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.originals.length - 1};
        if (!(event.key in destinations)) return;
        event.preventDefault();
        this.go(destinations[event.key]);
      }, this.options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAll();
        this.startMotion();
      }, this.options);
      this.measure();
      this.motionButton.hidden = this.originals.length <= 1;
      this.motionButton.addEventListener('click', () => { this.userPaused = !this.userPaused; this.updateMotionButton(); this.startMotion(); }, this.options);
      this.motionPreference.addEventListener('change', event => { this.userPaused = event.matches; this.updateMotionButton(); this.startMotion(); }, this.options);
      this.updateMotionButton();
      this.sectionVisibility = new IntersectionObserver(entries => {
        this.nearViewport = entries[0].isIntersecting;
        if (!this.nearViewport) this.pauseAll();
        this.startMotion();
      }, {rootMargin: '250px 0px', threshold: 0});
      this.sectionVisibility.observe(this.track);
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.track);
    }
    bindMedia(card) {
      const video = card.querySelector('video');
      if (!video) return;
      const play = card.querySelector('.bl-video-stories__play');
      const error = card.querySelector('.bl-video-stories__error');
      const poster = card.querySelector('.bl-video-stories__poster');
      video.controls = false;
      video.setAttribute('aria-hidden', 'true');
      video.removeAttribute('data-deferred');
      play.hidden = false;
      play.addEventListener('click', async () => {
        this.pauseUntil = performance.now() + 8000;
        this.stopMotion();
        this.startMotion();
        error.hidden = true;
        video.controls = true;
        video.removeAttribute('aria-hidden');
        play.hidden = true;
        try {
          // No video URL is attached until the customer explicitly presses play.
          if (!video.dataset.loaded) {
            video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
            if (video.dataset.src) video.src = video.dataset.src;
            if (video.dataset.poster) video.poster = video.dataset.poster;
            video.dataset.loaded = 'true';
            video.load();
          } else if (video.error) video.load();
          await video.play();
        } catch (playError) {
          play.hidden = false;
          video.controls = false;
          error.hidden = playError.name === 'AbortError';
        }
      }, this.options);
      video.addEventListener('play', () => {
        this.videos.forEach(other => { if (other !== video) other.pause(); });
        play.hidden = true;
        error.hidden = true;
        video.controls = true;
        this.startMotion();
      }, this.options);
      video.addEventListener('playing', () => { if (poster) poster.hidden = true; }, this.options);
      video.addEventListener('pause', () => { play.hidden = false; video.controls = false; this.startMotion(); }, this.options);
      video.addEventListener('ended', () => { play.hidden = false; video.controls = false; this.startMotion(); }, this.options);
      video.addEventListener('error', () => { play.hidden = false; error.hidden = false; }, this.options);
      this.visibility.observe(card);
      this.posterObserver.observe(card);
    }
    disconnectedCallback() {
      this.stopMotion();
      this.pauseAll();
      this.events?.abort();
      this.visibility?.disconnect();
      this.posterObserver?.disconnect();
      this.sectionVisibility?.disconnect();
      this.resize?.disconnect();
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.frame);
      clearTimeout(this.motionTimer);
      this.querySelectorAll('[data-loop-copy]').forEach(card => card.remove());
      this.originals?.forEach(card => { card.inert = false; });
    }
    pauseAll() { this.videos?.forEach(video => video.pause()); }
    measure() {
      if (!this.originals.length) return;
      const step = this.originals[0].getBoundingClientRect().width + 16;
      if (!step || this.lastWidth === this.track.clientWidth) return;
      this.lastWidth = this.track.clientWidth;
      this.measuring = true;
      this.stopMotion();
      this.pauseAll();
      this.step = step;
      const count = this.originals.length;
      const copies = count > 1 ? Math.ceil((this.track.clientWidth + step) / (count * step)) : 0;
      if (copies !== this.copies) {
        this.querySelectorAll('[data-loop-copy]').forEach(card => {
          this.visibility.unobserve(card);
          this.posterObserver.unobserve(card);
          card.remove();
        });
        this.copies = copies;
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        for (let group = 0; group < copies; group++) {
          this.originals.forEach(original => {
            before.append(this.cloneCard(original));
            after.append(this.cloneCard(original));
          });
        }
        this.rail.prepend(before);
        this.rail.append(after);
        this.querySelectorAll('[data-loop-copy]').forEach(card => this.bindMedia(card));
      }
      this.videos = [...this.track.querySelectorAll('video')];
      this.base = copies * count * step;
      this.period = count * step;
      this.jump(this.base + this.index * step);
      this.previous.hidden = this.next.hidden = count <= 1;
      this.previous.disabled = this.next.disabled = false;
      this.updateControls();
      this.measuring = false;
      this.startMotion();
    }
    cloneCard(original) {
      const card = original.cloneNode(true);
      card.dataset.loopCopy = '';
      card.removeAttribute('data-shopify-editor-block');
      card.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      card.querySelectorAll('video, source').forEach(element => {
        element.removeAttribute('src');
        element.removeAttribute('poster');
        element.removeAttribute('data-loaded');
      });
      card.querySelector('.bl-video-stories__poster')?.removeAttribute('hidden');
      card.querySelector('.bl-video-stories__poster')?.removeAttribute('src');
      card.querySelector('.bl-video-stories__poster')?.removeAttribute('srcset');
      card.querySelector('.bl-video-stories__error')?.setAttribute('hidden', '');
      return card;
    }
    jump(left) {
      this.track.classList.add('is-recentering');
      this.track.scrollTo({left, behavior: 'instant'});
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.track.classList.remove('is-recentering'));
    }
    recenter() {
      if (!this.copies || this.dragging || this.motionAnimation || this.videos?.some(video => !video.paused)) return;
      const offset = this.track.scrollLeft - this.base;
      if (offset >= -1 && offset < this.period) return;
      this.pauseAll();
      this.jump(this.base + ((offset % this.period) + this.period) % this.period);
      this.updateControls();
    }
    updateControls() {
      if (!this.step) return;
      const count = this.originals.length;
      const physical = Math.round((this.track.scrollLeft + this.motionOffset() - this.base) / this.step);
      this.index = ((physical % count) + count) % count;

    }
    go(index) {
      this.stopMotion();
      this.pauseUntil = performance.now() + 8000;
      this.track.classList.remove('is-auto-scrolling');
      this.pauseAll();
      this.recenter();
      this.track.scrollTo({left: this.base + index * this.step, behavior: this.motionPreference.matches ? 'instant' : 'smooth'});
      this.startMotion();
    }
    updateMotionButton() {
      this.motionButton.setAttribute('aria-pressed', String(this.userPaused));
      this.motionButton.setAttribute('aria-label', `${this.userPaused ? 'Start' : 'Pause'} automatic video scrolling`);
      this.motionButton.firstElementChild.textContent = this.userPaused ? '▶' : 'Ⅱ';
    }
    motionOffset() {
      if (!this.motionAnimation || !this.period) return 0;
      return ((this.motionAnimation.currentTime || 0) * this.speed) % this.period;
    }
    stopMotion() {
      if (!this.motionAnimation) return;
      // Preserve the visible position before handing control back to native swiping.
      const left = this.track.scrollLeft + this.motionOffset();
      this.motionAnimation.cancel();
      this.motionAnimation = null;
      this.jump(left);
      this.updateControls();
    }
    startMotion() {
      clearTimeout(this.motionTimer);
      if (this.measuring || !this.isConnected || !this.nearViewport || document.hidden || this.originals.length <= 1) {
        this.motionAnimation?.pause();
        return;
      }
      this.tick(performance.now());
    }
    tick(time) {
      const paused = this.userPaused || this.hovering || this.keyboardFocus || this.dragging || time < this.pauseUntil || this.videos.some(video => !video.paused);
      if (paused) {
        this.motionAnimation?.pause();
      } else {
        if (!this.motionAnimation) {
          this.track.classList.add('is-auto-scrolling');
          this.recenter();
          const offset = ((this.track.scrollLeft - this.base) % this.period + this.period) % this.period;
          this.jump(this.base);
          // A continuous transform runs on the compositor, avoiding whole-pixel scroll steps.
          this.motionAnimation = this.rail.animate([
            {transform: 'translate3d(0,0,0)'},
            {transform: `translate3d(${-this.period}px,0,0)`}
          ], {duration: this.period / this.speed, iterations: Infinity, easing: 'linear'});
          this.motionAnimation.currentTime = offset / this.speed;
        }
        if (this.motionAnimation.playState === 'paused') this.motionAnimation.play();
        this.updateControls();
      }
      // The compositor owns every frame. JS wakes only for user/media/visibility
      // events or the end of an interaction cooldown, never continuously.
      if (time < this.pauseUntil) {
        this.motionTimer = setTimeout(() => this.startMotion(), this.pauseUntil - time + 1);
      }
    }

  }
  if (!customElements.get('bl-video-stories')) customElements.define('bl-video-stories', BootyliftVideoStories);
})();
